<script setup lang="ts">
import { Modal } from "bootstrap";
import { computed, onMounted, useTemplateRef, watch } from "vue";

export type BaseModalProps = {
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  fullscreen?: "always" | "sm-down" | "md-down" | "lg-down" | "xl-down" | "xxl-down";
  minHeight?: string;
  isScrollable?: boolean;
  isCentered?: boolean;
};

const isOpen = defineModel<boolean>("modelValue", {
  default: true
});

const { title, size = "md", minHeight, fullscreen, isScrollable, isCentered } = defineProps<BaseModalProps>();

const emit = defineEmits<{
  willShow: [];
  didShow: [];
  willHide: [];
  didHide: [];
}>();

const $modal = useTemplateRef("$modal");
const instance = computed(() => $modal.value && Modal.getOrCreateInstance($modal.value));

onMounted(() => {
  $modal.value?.addEventListener("show.bs.modal", () => {
    emit("willShow");
  });

  $modal.value?.addEventListener("shown.bs.modal", () => {
    isOpen.value = true;
    emit("didShow");
  });

  $modal.value?.addEventListener("hide.bs.modal", () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    emit("willHide");
  });

  $modal.value?.addEventListener("hidden.bs.modal", () => {
    isOpen.value = false;
    emit("didHide");
  });

  watch(
    isOpen,
    (open) => {
      if (open) {
        instance.value?.show();
      } else {
        instance.value?.hide();
      }
    },
    { immediate: true }
  );
});

function close() {
  instance.value?.hide();
}
</script>

<template>
  <Teleport to="body">
    <div ref="$modal" class="modal fade" tabindex="-1" :aria-label="title">
      <div
        class="modal-dialog"
        :class="[
          `modal-${size}`,
          {
            'modal-fullscreen': fullscreen === 'always',
            'modal-fullscreen-sm-down': fullscreen === 'sm-down',
            'modal-fullscreen-md-down': fullscreen === 'md-down',
            'modal-fullscreen-lg-down': fullscreen === 'lg-down',
            'modal-fullscreen-xl-down': fullscreen === 'xl-down',
            'modal-fullscreen-xxl-down': fullscreen === 'xxl-down',
            'modal-dialog-scrollable': isScrollable,
            'modal-dialog-centered': isCentered
          }
        ]"
      >
        <div class="modal-content overflow-hidden" :style="{ minHeight }">
          <div class="modal-header">
            <h1 v-if="title" class="modal-title fs-5">{{ title }}</h1>
            <button type="button" class="btn-close" aria-label="Luk" @click="close()"></button>
          </div>
          <div class="modal-body">
            <slot v-if="isOpen" name="default" v-bind="{ close }" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
